import { SelectedReportsFormData } from '../../../_components/CheckoutForm';
import { MarketReportGroupVM } from '../../../_components/types';

/**
 * Extracts the ID from a prefixed key (e.g., 'group-daily-report' -> 'daily-report', 'foo-1' -> '1')
 */
export const extractIdFromKey = (key: string): string => {
    return key.substring(key.indexOf('-') + 1);
};

/**
 * Filters selected items to exclude:
 * 1. Items that are included in subscription (no priceId needed for checkout)
 * This ensures we don't double-count reports when calculating totals or sending to APIs.
 */
export const getFilteredItemsForCheckout = (
    selectedPrices: SelectedReportsFormData['selectedPrices'],
    reports?: MarketReportGroupVM[]
): Array<{ priceId: number; priceType: 'group' }> => {
    // Filter out:
    // 1. Items that are included in subscription
    return Object.entries(selectedPrices)
        .filter(([key, item]) => {
            const id = extractIdFromKey(key);

            // Skip reports that are included in subscription
            if (reports) {
                const report = reports.find((r) => `${r.id}` === id);
                if (report?.includedInSubscription ?? false) {
                    return false;
                }
            }

            return true;
        })
        .map(([_, item]) => ({
            priceId: item.priceId,
            priceType: item.type,
        }));
};

export const calculateTotal = (
    value: SelectedReportsFormData['selectedPrices'],
    initialSelectedPrices: SelectedReportsFormData['selectedPrices'],
    reports: MarketReportGroupVM[]
) => {
    // Calculate initial total (from initially selected items)
    const initialTotal = Object.keys(initialSelectedPrices).reduce((acc, selected) => {
        const item = initialSelectedPrices[selected];
        const id = extractIdFromKey(selected);

        if (item.type === 'group') {
            const report = reports.find((r) => `${r.id}` === id);
            // Skip if included in subscription (costs €0)
            if (report?.includedInSubscription ?? false) {
                return acc;
            }
            const price = report?.price;
            return acc + (price?.price || 0);
        }

        return acc;
    }, 0);

    // Calculate current total (all currently selected items)
    const currentTotal = Object.keys(value).reduce((acc, selected) => {
        const item = value[selected];
        const id = extractIdFromKey(selected);

        if (item.type === 'group') {
            const report = reports.find((r) => `${r.id}` === id);
            // Skip if included in subscription (costs €0)
            if (report?.includedInSubscription ?? false) {
                return acc;
            }
            const price = report?.price;
            return acc + (price?.price || 0);
        }

        return acc;
    }, 0);

    const totalChange = currentTotal - initialTotal;

    return {
        total: currentTotal,
        totalChange,
    };
};
